import React, {useState} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { Button, TextField } from '@mui/material';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { adminAddTheoryQuestion } from "../../api/Api";
import { useColors } from '../../theme/ColorModeContext';
import PageContainer from '../../shared/PageContainer';
import GlowCard from '../../shared/GlowCard';
import CyberpunkTitle from '../../shared/CyberpunkTitle';

interface PropsAdminTheoryQuestions {
    token: string;
}

const fieldSx = (colors: any) => ({
    '& .MuiOutlinedInput-root': {
        fontFamily: '"Share Tech Mono", monospace',
        fontSize: '0.9rem',
        color: colors.text.primary,
        '& fieldset': { borderColor: colors.border.default },
        '&:hover fieldset': { borderColor: colors.border.accent },
        '&.Mui-focused fieldset': { borderColor: colors.accent.cyan, boxShadow: `0 0 0 2px ${colors.accent.cyanGlow}` },
    },
    '& .MuiInputLabel-root': {
        fontFamily: '"Share Tech Mono", monospace',
        fontSize: '0.82rem',
        letterSpacing: '0.08em',
        color: colors.text.secondary,
        '&.Mui-focused': { color: colors.accent.cyan },
    },
});

const selectSx = (colors: any) => ({
    fontFamily: '"Share Tech Mono", monospace',
    fontSize: '0.9rem',
    color: colors.text.primary,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border.default },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.border.accent },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent.cyan },
    '& .MuiSvgIcon-root': { color: colors.text.secondary },
});

const AdminAddTheoryQuestion = (props: PropsAdminTheoryQuestions) => {
    const colors = useColors();
    const navigate = useNavigate();
    const { stage_id } = useParams();
    const [processing, setProcessing] = useState(false);
    const [message, setMessage] = useState("");
    const [orderIndex, setOrderIndex] = useState(0);
    const [question, setQuestion] = useState("");
    const [choiceA, setChoiceA] = useState("");
    const [choiceB, setChoiceB] = useState("");
    const [choiceC, setChoiceC] = useState("");
    const [choiceD, setChoiceD] = useState("");
    const [correctChoice, setCorrectChoice] = useState("a");
    const [explanation, setExplanation] = useState("");

    const token = props.token;

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        setProcessing(true);
        const response = await adminAddTheoryQuestion(token, stage_id, {
            order_index: orderIndex,
            question,
            choice_a: choiceA,
            choice_b: choiceB,
            choice_c: choiceC,
            choice_d: choiceD,
            correct_choice: correctChoice,
            explanation,
        });
        setProcessing(false);
        setMessage(response.msg);
        if (response.msg === "Question created") {
            navigate(`/admin/theory/stage/${stage_id}/questions`);
        }
    };

    return (
        <PageContainer>
            <CyberpunkTitle
                segments={[
                    { label: 'Theory Stages', to: '/admin/theory/stages' },
                    { label: 'Questions', to: `/admin/theory/stage/${stage_id}/questions` },
                    { label: 'Add' },
                ]}
                mb="32px"
            />

            <GlowCard accentTop glowOnHover={false} sx={{ maxWidth: '680px' }}>
                <Box sx={{ padding: '32px' }}>
                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            <TextField
                                sx={{ ...fieldSx(colors), width: '140px' }}
                                required
                                label="Order"
                                type="number"
                                value={orderIndex}
                                onChange={(e) => setOrderIndex(Number(e.target.value))}
                                helperText="Position within the stage (0-based)"
                            />
                            <TextField
                                sx={fieldSx(colors)}
                                required
                                fullWidth
                                multiline
                                minRows={2}
                                label="Question"
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                            />
                            <TextField sx={fieldSx(colors)} required fullWidth label="Choice A" value={choiceA} onChange={(e) => setChoiceA(e.target.value)} />
                            <TextField sx={fieldSx(colors)} required fullWidth label="Choice B" value={choiceB} onChange={(e) => setChoiceB(e.target.value)} />
                            <TextField sx={fieldSx(colors)} required fullWidth label="Choice C" value={choiceC} onChange={(e) => setChoiceC(e.target.value)} />
                            <TextField sx={fieldSx(colors)} required fullWidth label="Choice D" value={choiceD} onChange={(e) => setChoiceD(e.target.value)} />

                            <FormControl sx={{ width: '160px' }}>
                                <InputLabel sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.82rem', color: colors.text.secondary, '&.Mui-focused': { color: colors.accent.cyan } }}>
                                    Correct Choice
                                </InputLabel>
                                <Select
                                    value={correctChoice}
                                    label="Correct Choice"
                                    onChange={(e) => setCorrectChoice(e.target.value)}
                                    sx={selectSx(colors)}
                                    MenuProps={{ PaperProps: { sx: { backgroundColor: colors.bg.surface, color: colors.text.primary } } }}
                                >
                                    {['a', 'b', 'c', 'd'].map((c) => (
                                        <MenuItem key={c} value={c} sx={{ fontFamily: '"Share Tech Mono", monospace' }}>{c.toUpperCase()}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                sx={fieldSx(colors)}
                                fullWidth
                                multiline
                                minRows={2}
                                label="Explanation (optional)"
                                helperText="Shown after a correct answer"
                                value={explanation}
                                onChange={(e) => setExplanation(e.target.value)}
                            />

                            <Button
                                type="submit"
                                fullWidth
                                variant="outlined"
                                disabled={processing}
                                startIcon={processing ? <CircularProgress size={16} sx={{ color: colors.accent.cyan }} /> : <CheckCircleOutlineIcon />}
                                sx={{
                                    marginTop: '8px',
                                    borderColor: colors.accent.cyan,
                                    color: colors.accent.cyan,
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    padding: '12px',
                                    borderRadius: '6px',
                                }}
                            >
                                {processing ? 'Adding...' : 'Add Question'}
                            </Button>

                            {message && (
                                <Box sx={{
                                    padding: '12px 16px',
                                    borderRadius: '6px',
                                    backgroundColor: colors.accent.cyanGlow,
                                    border: `1px solid ${colors.accent.cyan}44`,
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontSize: '0.8rem',
                                    color: colors.accent.cyan,
                                }}>
                                    {message}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </GlowCard>
        </PageContainer>
    );
};

export default AdminAddTheoryQuestion;
